import { Injectable } from '@angular/core';
import { AccessRightService } from './access-right.service';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightUserFilter,
    ILightWeightArea,
    ILightWeightObject,
    ILightWeightUser,
    ILocalSearchDataItemModel,
    LocalSearchItemModel,
} from '../models';
import { AppTextService } from '@fi-shared/app-text';
import { FiCheckbox2AdapterService } from '@fi-shared/fi-checkbox2/fi-checkbox2-adapter.service';
import { IFiCheckBoxChangeEvent, IFiCheckboxItem } from '@fi-shared/fi-checkbox2/fi-checkbox2';
import { FiDropdownActionResult } from '@fi-shared/fi-select-search/fi-select-search';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ModuleAccessLevel } from '@fi-shared/access-right-filters/module-access-level';

export interface ICanChangeAccessRightData {
    $event: IFiCheckBoxChangeEvent;
    objectType: ObjectTypes;
    notAvailableItemIds: number[];
    selectedItemIds: number[];
}

export interface IIsBusinessAreaDisabledArgs {
    businessAreaId: number;
    initialSelectedBusinessAreaIds: number[];
    businessAreaIds: number[];
    notAvailableBusinessAreas: ILightWeightObject[];
}

export interface IAccessRightsCoreFiltersErrors {
    role_required?: boolean;
    business_area_required?: boolean;
    area_or_unit_or_cluster_required?: boolean;
    area_or_cluster_required?: boolean;
    selection_filter_required?: boolean;
}

@Injectable()
export class FiAccessRightHelperService {
    constructor(
        private accessRightService: AccessRightService,
        private appTextService: AppTextService,
        private checkbox2AdapterService: FiCheckbox2AdapterService,
        private fiDialogsService: FiDialogsService,
    ) {}

    getOutOfScopeItems(
        availableItems: ILightWeightObject[],
        existingItems: ILightWeightObject[],
    ): ILightWeightObject[] {
        const availableItemIds = availableItems.map((i) => i.Id);
        return (existingItems ?? []).filter((item) => availableItemIds.indexOf(item.Id) === -1);
    }

    getOutOfScopeUsers(availableItems: ILocalSearchDataItemModel[], existingItems: ILightWeightUser[]) {
        const availableItemIds = availableItems.map((i) => i.Id);
        return (existingItems ?? []).filter((item) => availableItemIds.indexOf(item.Id) === -1);
    }

    getOutOfScopeClusterAndUnit(availableItems: ILocalSearchDataItemModel[], existingItems: ILightWeightObject[]) {
        const availableItemIds = availableItems.map((i) => i.Id);
        return (existingItems ?? []).filter((item) => availableItemIds.indexOf(item.Id) === -1);
    }

    getOutOfScopeAreas(availableAreas: ILightWeightArea[], existingAreas: ILightWeightArea[]): ILightWeightArea[] {
        const availableAreaIds = availableAreas.map((i) => i.Id);
        return (existingAreas ?? []).filter((item) => availableAreaIds.indexOf(item.Id) === -1);
    }

    getAreaWithChildrenIds(areas: ILightWeightArea[]): number[] {
        let result = areas.map((area) => area.Id);
        areas
            .filter((area) => area.ChildItems && area.ChildItems.length)
            .forEach((area) => (result = result.concat(this.getAreaWithChildrenIds(area.ChildItems))));
        return result;
    }

    getAreaCheckboxListData(availableAreas: ILightWeightArea[], outOfScopeAreas: ILightWeightArea[]): IFiCheckboxItem {
        return {
            id: null,
            name: this.appTextService.get('common.areas'),
            header: true,
            childItems: this.checkbox2AdapterService
                .mapLightWeightAreas(availableAreas, (item) => (item.IsSystemObject ? 'red' : undefined))
                .concat(this.checkbox2AdapterService.mapLightWeightAreas(outOfScopeAreas, () => 'red')),
        };
    }

    getLightWeightItemCheckboxListData(
        availableItems: ILightWeightObject[],
        outOfScopeItems: ILightWeightObject[],
        name: string,
    ): IFiCheckboxItem {
        return {
            id: null,
            name,
            header: true,
            childItems: this.checkbox2AdapterService
                .mapLightWeightObjects(availableItems, (item) => (item.IsSystemObject ? 'red' : undefined))
                .concat(this.checkbox2AdapterService.mapLightWeightObjects(outOfScopeItems, () => 'red')),
        };
    }

    canRemoveExtendedSharing(
        outOfScopeItemsIds: number[],
        item: LocalSearchItemModel,
    ): Promise<FiDropdownActionResult> {
        if (!outOfScopeItemsIds.find((outOfScopeItemsId) => outOfScopeItemsId === item.Id)) {
            return Promise.resolve({ result: true });
        }
        return new Promise((resolve, reject) => {
            this.deletingUnavailableAccessRightItemConfirm(item.ObjectType, item.Name).then((result) => {
                resolve({ result: result });
            });
        });
    }

    canChangeAccessRight(data: ICanChangeAccessRightData): Promise<boolean> {
        if (data.$event.selected) return Promise.resolve(true);

        // show warning message only on unchecking not available items

        if (!data.$event.header && data.notAvailableItemIds.indexOf(data.$event.id) === -1)
            return Promise.resolve(true);

        // don't show warning on click on header if all selected items are available
        if (
            data.$event.header &&
            !data.selectedItemIds.some((selectedItemId) => {
                return data.notAvailableItemIds.indexOf(selectedItemId) !== -1;
            })
        )
            return Promise.resolve(true);

        return this.deletingUnavailableAccessRightItemConfirm(data.objectType, undefined);
    }

    deletingUnavailableAccessRightItemConfirm(objectType: ObjectTypes, objectName: string): Promise<boolean> {
        let headerTextId = 'access_right.uncheck_not_shared_header';
        let messageTextId = (() => {
            if (objectType === ObjectTypes.User) return 'access_right.uncheck_not_shared_message_user';
            if (objectType === ObjectTypes.Role) return 'access_right.uncheck_not_shared_message_den';
            return 'access_right.uncheck_not_shared_message_det';
        })();

        const objectTypeName = this.appTextService.get(getObjectTypeNameTextId(objectType)).toLowerCase();
        return new Promise<boolean>((resolve, reject) => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService
                        .get(headerTextId)
                        .replace('{type_name}', objectType !== ObjectTypes.User ? objectTypeName : objectName),
                    messageText: this.appTextService.get(messageTextId).replace('{type_name}', objectTypeName),
                    yesButtonText: this.appTextService.get('common.remove'),
                })
                .onYesButtonClick(() => resolve(true))
                .onNoButtonClick(() => resolve(false));
        });
    }

    getLightWeightObjectsFromAreas(areas: ILightWeightArea[]) {
        const result: ILightWeightObject[] = [];
        areas.forEach((area) => {
            result.push({
                Id: area.Id,
                Name: area.Name,
                IsSystemObject: area.IsSystemObject,
                ParentId: area.ParentId,
            });
            result.push(...this.getLightWeightObjectsFromAreas(area.ChildItems));
        });
        return result;
    }

    isBusinessAreaDisabled(args: IIsBusinessAreaDisabledArgs) {
        return (
            args.initialSelectedBusinessAreaIds.includes(args.businessAreaId) ||
            (args.businessAreaIds.length === 1 && !args.notAvailableBusinessAreas?.length)
        );
    }

    isBusinessUnitDisabled(
        businessUnitId: number,
        notAvailableBusinessUnitIds: number[],
        deactivatedBusinessUnitIds: number[],
    ) {
        return (
            notAvailableBusinessUnitIds.indexOf(businessUnitId) !== -1 ||
            deactivatedBusinessUnitIds.indexOf(businessUnitId) !== -1
        );
    }

    getBusinessUnitTitle(businessUnitId: number, deactivatedBusinessUnitIds: number[]) {
        /*if (this.notAvailableUserIds.indexOf(item.Id) !== -1)
            return '';*/
        if (deactivatedBusinessUnitIds.indexOf(businessUnitId) !== -1)
            return this.appTextService.get('common.deactivated');
        return undefined;
    }

    isUserDisabled(userId: number, notAvailableUserIds: number[], deactivatedUserIds: number[]) {
        return notAvailableUserIds.indexOf(userId) !== -1 || deactivatedUserIds.indexOf(userId) !== -1;
    }

    getUserTitle(userId: number, deactivatedUserIds: number[]) {
        /*if (this.notAvailableUserIds.indexOf(item.Id) !== -1)
            return '';*/
        if (deactivatedUserIds.indexOf(userId) !== -1) return this.appTextService.get('common.deactivated');
        return undefined;
    }

    getExtendedSharingItemClassName(
        itemId: number,
        outOfScopeItemIds: number[],
        systemItemIds: number[],
    ): {
        [key: string]: boolean;
    } {
        return {
            red: outOfScopeItemIds.includes(itemId) || systemItemIds.includes(itemId),
        };
    }

    checkSelectSingleBusinessArea(
        availableCoreFilter: IAccessRightCoreFilter,
        itemCoreFilter: IAccessRightCoreFilter,
        onSelectedBusinessAreasChangedFn: () => void,
    ) {
        const availableBusinessAreaIds = availableCoreFilter.BusinessAreaIds;

        // https://chainformation.atlassian.net/browse/FI-5011
        if (availableBusinessAreaIds.length === 1) {
            // https://chainformation.atlassian.net/browse/FI-3911
            const businessAreaId = availableBusinessAreaIds[0];
            if (!itemCoreFilter.BusinessAreaIds.find((id) => id !== businessAreaId)) {
                // https://chainformation.atlassian.net/browse/FI-6260
                const isBusinessAreaSelected = !!itemCoreFilter.BusinessAreaIds.find((id) => id === businessAreaId);
                const businessAreaShouldBeSelected =
                    itemCoreFilter.AreaIds.length > 0 || itemCoreFilter.ClusterIds.length > 0;

                if (isBusinessAreaSelected && !businessAreaShouldBeSelected) {
                    itemCoreFilter.BusinessAreaIds = [];
                    onSelectedBusinessAreasChangedFn();
                } else if (!isBusinessAreaSelected && businessAreaShouldBeSelected) {
                    itemCoreFilter.BusinessAreaIds = [businessAreaId];
                    onSelectedBusinessAreasChangedFn();
                }
            }
        }
    }

    validateAccessRightsCoreFilters(
        accessRightCoreFilter: IAccessRightCoreFilter,
        args: {
            isShared: boolean;
            onlyRolesMode?: boolean;
            skipRolesCheck?: boolean;
            accessRightUserFilter?: IAccessRightUserFilter;
        },
    ): IAccessRightsCoreFiltersErrors {
        if (!args) {
            args = {
                isShared: true,
            };
        }

        const userIds: number[] = args.accessRightUserFilter?.UserIds ?? [];

        // valid selection, no errors in this case
        const onlyUsersOrNotSharedSelection =
            (userIds.length > 0 || !args.isShared) &&
            accessRightCoreFilter.RoleIds.length === 0 &&
            accessRightCoreFilter.AreaIds.length === 0 &&
            accessRightCoreFilter.ClusterIds.length === 0 &&
            accessRightCoreFilter.BusinessUnitIds.length == 0;

        let errors: IAccessRightsCoreFiltersErrors = {};
        if (!args.skipRolesCheck && accessRightCoreFilter.RoleIds.length === 0 && !onlyUsersOrNotSharedSelection) {
            errors.role_required = true;
        }

        if (!args.onlyRolesMode) {
            if (
                accessRightCoreFilter.BusinessAreaIds.length === 0 &&
                (accessRightCoreFilter.AreaIds.length > 0 || accessRightCoreFilter.ClusterIds.length > 0)
            ) {
                errors.business_area_required = true;
            }

            if (
                accessRightCoreFilter.BusinessAreaIds.length > 0 &&
                accessRightCoreFilter.AreaIds.length === 0 &&
                accessRightCoreFilter.ClusterIds.length === 0
            ) {
                errors.area_or_cluster_required = true;
            }

            if (
                accessRightCoreFilter.AreaIds.length === 0 &&
                accessRightCoreFilter.ClusterIds.length === 0 &&
                accessRightCoreFilter.BusinessUnitIds.length == 0 &&
                !onlyUsersOrNotSharedSelection
            ) {
                errors.area_or_unit_or_cluster_required = true;
            }
        }

        return errors;
    }

    isBusinessAreasSelectionAvailable(
        availableAccessRightCoreFilter: IAccessRightCoreFilter,
        itemsAccessRightCoreFilter: IAccessRightCoreFilter,
        args?: {
            ignoreClusters?: boolean;
        },
    ): boolean {
        if (!args) {
            args = {};
        }
        // https://chainformation.atlassian.net/browse/FI-6260
        // hide business areas if user has no areas and clusters available for selection
        // and there are not available for current user items (added by other user with higher rights)
        // ignore clusters for schedule access rights
        return (
            availableAccessRightCoreFilter.AreaIds?.length > 0 ||
            itemsAccessRightCoreFilter.AreaIds?.length > 0 ||
            itemsAccessRightCoreFilter.BusinessAreas?.length > 0 ||
            (!args.ignoreClusters && availableAccessRightCoreFilter.ClusterIds?.length > 0) ||
            (!args.ignoreClusters && itemsAccessRightCoreFilter.ClusterIds?.length > 0)
        );
    }

    mergeLightWeightObjects<T extends ILightWeightObject>(items1: T[], items2: T[]) {
        const result = [...items1];
        const existingIds = items1.map((item) => item.Id);
        items2.filter((item) => existingIds.indexOf(item.Id) === -1).forEach((item) => result.push(item));
        return result;
    }
}
